import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Coins, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pay-as-you-go USD pricing. No subscriptions, no packages.
// Protocol: DEPLOY-MAP.md 28.06.2026
const OP_LABELS = {
    brand_monitor: 'Brand Monitoring',
    sub_monitor: 'Product / Service Monitoring',
    geo_monitor: null,
    auto_monitor: null,
    custom_query: 'Custom Query',
    create_project: null,
    chat_summarize: null,
    pdf_export: null,
    hypothesis_early_check: 'Hypothesis Check',
    geo_audit: 'GEO Audit',
    generate_strategy: 'Strategy Generation',
    chat_message: 'Chatbot Message',
    chat_parse_url: 'URL Parsing in Chat',
    analyze_sources: 'Source Analysis',
    generate_topics: 'Topic Generation',
    generate_topics_url: 'Topics from URL',
    generate_article: 'Article Writing',
    improve_article: 'Article Improvement',
    get_insights: 'Strategic Insights',
};

const PricingPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [operationCosts, setOperationCosts] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const costsRes = await axios.get(`${API_URL}/api/billing/operation-costs`, { withCredentials: true });
            setOperationCosts(costsRes.data);
        } catch (err) {
            console.error('Pricing load error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Format cents to USD: 1200 -> "$12.00", 50 -> "$0.50"
    const fmtUSD = (cents) => {
        if (cents === null || cents === undefined) return '$0.00';
        return '$' + (cents / 100).toFixed(2);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-lg text-slate-500">Loading...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white" data-testid="pricing-page">
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#E2DDD8] sticky top-0 z-50">
                <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/dashboard')} className="text-slate-600 hover:text-slate-900" data-testid="pricing-back-btn">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <img src="/arpai_logo.png" alt="ARPAI" style={{height: '1.25rem'}} />
                        <span className="text-xl font-bold text-[#2F2E2F] tracking-[0.15em]">GEO</span>
                        <span className="hidden sm:inline text-[10px] text-slate-400 ml-2">AI visibility monitoring and analytics service</span>
                        <span className="text-slate-300 mx-1">/</span>
                        <h1 className="text-base sm:text-lg tracking-tight font-bold text-slate-500">Pricing</h1>
                    </div>
                </div>
            </header>

            <main className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-12">
                {/* Info banner */}
                <div className="bg-[#F5F3EE] border border-[#E2DDD8] p-6 mb-10">
                    <p className="text-sm text-slate-700">
                        <strong>Pay as you go.</strong>{' '}
                        All prices in USD. Funds never expire. No subscriptions.
                    </p>
                </div>

                {/* Top-up info */}
                <div className="bg-[#F5F3EE] border border-[#E2DDD8] p-6 mb-16">
                    <h3 className="text-lg font-bold text-[#2F2E2F] mb-2">Top Up Balance</h3>
                    <p className="text-sm text-slate-700">
                        Top up from $25 to $1,000. Pay only for what you use.
                    </p>
                </div>

                {/* Operation costs table */}
                <h2 className="text-2xl font-bold text-[#2F2E2F] mb-8">Operation Costs</h2>
                <div className="bg-white border border-[#E2DDD8] overflow-hidden" data-testid="op-costs-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-[#E2DDD8] bg-[#F5F3EE]">
                                <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Operation</th>
                                <th className="text-right px-6 py-4 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Cost ($)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(operationCosts)
                                .filter(([key, v]) => v > 0 && OP_LABELS[key] !== null && OP_LABELS[key] !== undefined)
                                .sort((a, b) => b[1] - a[1])
                                .map(([key, cost]) => (
                                <tr key={key} className="border-b border-[#E2DDD8] last:border-0 hover:bg-[#F5F3EE]/50 transition-colors">
                                    <td className="px-6 py-4 text-slate-700 font-medium">{OP_LABELS[key] || key}</td>
                                    <td className="px-6 py-4 text-right font-semibold text-slate-900">
                                        <span className="inline-flex items-center gap-1">
                                            <DollarSign className="w-3.5 h-3.5 text-[#1BB594]" /> {(cost / 100).toFixed(2)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </main>
        </div>
    );
};

export default PricingPage;
